/**
 * DobField — locale-independent date-of-birth picker (Day / Month / Year).
 * Avoids the ambiguous US mm/dd/yyyy native date input so that, for an
 * Australian / international audience, the date reads unambiguously
 * (e.g. 20 / February / 1990). Outputs an ISO "YYYY-MM-DD" string via onChange
 * (or "" when incomplete) — backend storage format is unchanged.
 *
 * The year list is capped to 18+ birthdates (matches the 18+ gate); the backend
 * still validates age independently, so the gate logic stays intact.
 */
import { useEffect, useMemo, useState } from "react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function formatDobLong(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!MONTHS[m - 1]) return "";
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export default function DobField({ value = "", onChange, theme = "light", testId = "dob" }) {
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(value);
  // Internal part state so partial selections persist (an ISO is only emitted once complete).
  const [d, setD] = useState(valid ? Number(value.slice(8, 10)) : "");
  const [m, setM] = useState(valid ? Number(value.slice(5, 7)) : "");
  const [y, setY] = useState(valid ? Number(value.slice(0, 4)) : "");

  // Keep internal state in sync if the parent clears/sets the value externally.
  useEffect(() => {
    if (valid) {
      setY(Number(value.slice(0, 4)));
      setM(Number(value.slice(5, 7)));
      setD(Number(value.slice(8, 10)));
    } else if (value === "") {
      setD(""); setM(""); setY("");
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const now = new Date();
  const maxYear = now.getFullYear() - 18;
  const minYear = now.getFullYear() - 100;
  const years = useMemo(() => {
    const arr = [];
    for (let yr = maxYear; yr >= minYear; yr--) arr.push(yr);
    return arr;
  }, [maxYear, minYear]);

  const daysInMonth = m && y ? new Date(y, m, 0).getDate() : 31;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const emit = (nd, nm, ny) => {
    if (nd && nm && ny) {
      onChange?.(`${ny}-${String(nm).padStart(2, "0")}-${String(nd).padStart(2, "0")}`);
    } else {
      onChange?.("");
    }
  };

  const handleDay = (e) => { const nd = Number(e.target.value) || ""; setD(nd); emit(nd, m, y); };
  const handleMonth = (e) => {
    const nm = Number(e.target.value) || "";
    const maxD = nm && y ? new Date(y, nm, 0).getDate() : 31;
    const nd = d && d > maxD ? maxD : d;
    setM(nm); setD(nd); emit(nd, nm, y);
  };
  const handleYear = (e) => {
    const ny = Number(e.target.value) || "";
    const maxD = m && ny ? new Date(ny, m, 0).getDate() : 31;
    const nd = d && d > maxD ? maxD : d;
    setY(ny); setD(nd); emit(nd, m, ny);
  };

  const dark = theme === "dark";
  const selStyle = dark
    ? { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(196,163,224,0.22)", color: "#F3ECFA", colorScheme: "dark" }
    : { background: "#FFFFFF", border: "1px solid #E6E2DA", color: "var(--charcoal, #1A1614)" };
  const base = "h-10 rounded-2xl px-2.5 text-sm focus:outline-none appearance-none";

  return (
    <div className="flex gap-2" data-testid={`${testId}-field`}>
      <select aria-label="Day" value={d || ""} onChange={handleDay}
        className={`${base} flex-1`} style={selStyle} data-testid={`${testId}-day`}>
        <option value="">Day</option>
        {days.map((dd) => <option key={dd} value={dd}>{dd}</option>)}
      </select>
      <select aria-label="Month" value={m || ""} onChange={handleMonth}
        className={`${base} flex-[1.5]`} style={selStyle} data-testid={`${testId}-month`}>
        <option value="">Month</option>
        {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
      </select>
      <select aria-label="Year" value={y || ""} onChange={handleYear}
        className={`${base} flex-[1.2]`} style={selStyle} data-testid={`${testId}-year`}>
        <option value="">Year</option>
        {years.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
}
